// src/ui/Transport.js
// Play/Pause + Stop + BPM slider + position display.
//
// Transport model:
//   - Play/Pause: one button. Shows ▶ when stopped, ⏸ when playing.
//     Click to start playback. While playing, click again to pause at
//     the current position. Future-scheduled events are preserved so
//     the next click resumes in place. The progress line does NOT
//     reset on pause.
//   - Stop: pauses the timeline, cancels every scheduled note
//     (including future-scheduled ones), silences live voices, and
//     returns the progress line to 0:00.0. The next Play starts a
//     fresh arrangement from the start.

import * as Tone from 'tone';
import { engine } from '../audio/engine.js';
import { recorder } from './recorder.js';
import { t, onLocaleChange } from '../i18n.js';
import { clipLibrary } from '../clips/ClipLibrary.js';

export class Transport {
  /**
   * @param {HTMLElement} rootEl
   * @param {import('../tracks/TrackManager.js').TrackManager} trackManager
   * @param {import('../sequencer/Sequencer.js').Sequencer} [sequencer]
   *   Optional. Used to check which track is selected before recording.
   */
  constructor(rootEl, trackManager, sequencer) {
    this.root = rootEl;
    this.trackManager = trackManager;
    this.sequencer = sequencer;
    this.isPlaying = false;
    this.scheduledEndId = null;
    this._countdownInterval = null;
    this._countdownEscapeHandler = null;
    this._warnTimer = null;
    // AudioContext time at the last pause. Used by play() to compute
    // the time shift when re-scheduling the snapshot'd sources.
    this._pauseTime = 0;
    // Registered Tone.Transport 'loop' event handler, used to
    // re-schedule notes at each loop boundary. Null when loop
    // is off. Created in _registerLoopHandler, cleaned up by
    // _unregisterLoopHandler (called from stop/pause/play).
    this._loopHandler = null;
    this.render();

    onLocaleChange(() => this.render());

    this._tick = this._tick.bind(this);
    requestAnimationFrame(this._tick);
  }

  render() {
    this.root.innerHTML = `
      <div class="transport-row">
        <button id="btn-play" class="transport-btn" title="${t('transport.play')}">▶</button>
        <button id="btn-stop" class="transport-btn" title="${t('transport.stop')}">■</button>
        <button id="btn-loop" class="transport-btn" title="${t('transport.loop')}">⟲</button>
        <div class="transport-tempo">
          <label>${t('transport.bpm')}</label>
          <input id="bpm-input" type="number" min="40" max="240" value="100" />
        </div>
        <input class="transport-position" id="position-input" type="text" value="0:00.0" title="Click to edit, drag on ruler to scrub" />
        <div class="transport-global">
          <label>${t('transport.reverb')}</label>
          <input id="reverb-wet" type="range" min="0" max="1" step="0.01" value="0.3" />
          <label>${t('transport.humanize')}</label>
          <input id="humanize" type="range" min="0" max="0.05" step="0.001" value="0.012" />
        </div>
      </div>
    `;

    // The countdown overlay lives at the document level (#record-countdown
    // in index.html) so it can sit above the whole workspace.
    this.countdownEl = document.getElementById('record-countdown');
    this.countdownNumberEl = this.countdownEl
      ? this.countdownEl.querySelector('.record-countdown-number')
      : null;

    this.root.querySelector('#btn-play').addEventListener('click', () => this.togglePlay());
    this.root.querySelector('#btn-stop').addEventListener('click', () => this.stop());
    this.root.querySelector('#btn-loop').addEventListener('click', (e) => {
      // Toggle loop on the transport. The Tone.Transport constructor
      // sets _loopEnd = 0 internally, so when loop is enabled we must
      // also set loopEnd to the arrangement length. Without this, the
      // wrap check (ticks >= _loopEnd) fires on every tick (0 >= 0 is
      // always true), wrapping the playhead back to 0 before it can
      // ever advance — the progress line appears frozen.
      Tone.Transport.loop = !Tone.Transport.loop;
      if (Tone.Transport.loop) {
        const len = this.trackManager.getArrangementLength();
        if (len > 0) {
          Tone.Transport.loopStart = 0;
          Tone.Transport.loopEnd = len;
        }
        e.currentTarget.classList.add('active');
      } else {
        e.currentTarget.classList.remove('active');
      }
    });

    // Render the record button into the piano panel bar (next to the
    // Piano heading) so it's visually associated with the keyboard,
    // not the transport controls.
    this._renderRecordButton();
    // Alt+R toggles record mode (but not while typing in an input).
    // Alt is required so the bare R key doesn't trigger recording by
    // accident while the user is just typing.
    window.addEventListener('keydown', (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey || e.metaKey) return;
      if (!e.altKey) return;
      if (e.code === 'KeyR') {
        // Mirror click logic so the shortcut triggers the same countdown
        // path (and the same "no track selected" guard).
        this._onRecordClick();
      }
    });
    // Keep the Record button's .active class in sync with the shared
    // recorder state (e.g. if it changes elsewhere in the future).
    recorder.onChange((on) => {
      if (this.recordBtn) this.recordBtn.classList.toggle('active', on);
    });
    // Editable position input — type a time (MM:SS.s or seconds) to jump.
    const posInput = this.root.querySelector('#position-input');
    const commitPosition = (val) => {
      let sec = parseFloat(val);
      if (isNaN(sec)) {
        const m = val.match(/^(\d+):(\d+(?:\.\d+)?)$/);
        if (m) sec = parseInt(m[1], 10) * 60 + parseFloat(m[2]);
      }
      if (!isNaN(sec) && sec >= 0) {
        Tone.Transport.seconds = sec;
      }
      posInput.value = formatTime(Tone.Transport.seconds || 0);
    };
    posInput.addEventListener('change', (e) => commitPosition(e.target.value));
    posInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.target.blur(); // triggers change, which calls commitPosition
      }
    });
    this.root.querySelector('#bpm-input').addEventListener('change', (e) => {
      const v = Math.max(40, Math.min(240, +e.target.value || 100));
      engine.setBpm(v);
    });
    this.root.querySelector('#reverb-wet').addEventListener('input', (e) => {
      engine.setReverbWet(+e.target.value);
    });
    this.root.querySelector('#humanize').addEventListener('input', (e) => {
      engine.setHumanize({ timing: +e.target.value });
    });
  }

  togglePlay() {
    if (this.isPlaying) this.pause();
    else this.play();
  }

  /**
   * Record-button click handler. Three branches:
   *   1. Already recording → cancel any in-flight countdown and turn off.
   *   2. No track selected → show a brief "Select a track first" warning.
   *   3. Normal path → stop any in-progress playback, reset the playhead
   *      to 0, run a 3-second countdown, then enable recording and start
   *      playback. The "always run the countdown" behavior was requested
   *      by the user even if transport is already rolling.
   */
  _onRecordClick() {
    if (recorder.recording) {
      this._cancelCountdown();
      // Stop and finalize clip recording
      if (recorder.pendingClip) {
        const result = recorder.stopClipRecording();
        if (result && result.notes.length > 0) {
          // Normalize notes so the earliest starts at time 0.
          const earliest = Math.min(...result.notes.map((n) => n.time));
          if (earliest > 0) {
            for (const n of result.notes) n.time -= earliest;
          }
          const def = clipLibrary.addClip(result.name, result.notes);
          const selectedTrack = this.sequencer ? this.trackManager.getTrack(this.sequencer.selectedTrackId) : null;
          if (selectedTrack) {
            // Place the clip instance at recStartTime + earliest, so the
            // first note lands at the same position it was recorded.
            selectedTrack.addClipInstance(def.id, (recorder.recStartTime || 0) + earliest, 1);
          }
        }
      } else {
        recorder.setRecording(false);
      }
      return;
    }
    const sel = this.sequencer && this.sequencer.selectedTrackId;
    if (!sel || !this.trackManager.getTrack(sel)) {
      this._showRecordWarn(t('transport.selectTrackFirst'));
      return;
    }
    if (this.isPlaying) this.stop();
    Tone.Transport.seconds = 0;
    this._startCountdown(() => {
      Tone.Transport.seconds = 0;
      recorder.startClipRecording(`Clip ${clipLibrary.definitions.length + 1}`);
      this.play();
    });
  }

  /**
   * Show the 3-2-1 overlay, then call onComplete. Each visible tick
   * restarts the pulse animation by toggling `animation: none` and
   * forcing a reflow — the css keyframes are otherwise one-shot.
   */
  _startCountdown(onComplete) {
    this._cancelCountdown();
    const el = this.countdownEl;
    const num = this.countdownNumberEl;
    if (!el || !num) { onComplete(); return; }
    let n = 3;
    num.textContent = String(n);
    el.classList.remove('hidden');
    this._countdownInterval = setInterval(() => {
      n -= 1;
      if (n > 0) {
        num.textContent = String(n);
        // Restart the pulse animation.
        num.style.animation = 'none';
        // Force a reflow so the browser registers the animation reset.
        void num.offsetWidth;
        num.style.animation = '';
      } else {
        this._cancelCountdown();
        onComplete();
      }
    }, 1000);
    this._countdownEscapeHandler = (e) => {
      if (e.key === 'Escape') this._cancelCountdown();
    };
    document.addEventListener('keydown', this._countdownEscapeHandler);
  }

  /**
   * Tear down the countdown overlay, clear its interval, and remove
   * the Escape listener. Safe to call multiple times.
   */
  _cancelCountdown() {
    if (this._countdownInterval) {
      clearInterval(this._countdownInterval);
      this._countdownInterval = null;
    }
    if (this._countdownEscapeHandler) {
      document.removeEventListener('keydown', this._countdownEscapeHandler);
      this._countdownEscapeHandler = null;
    }
    if (this.countdownEl) this.countdownEl.classList.add('hidden');
  }

  /**
   * Briefly display a warning message next to the Record button.
   * Useful for the "no track selected" guard.
   */
  _showRecordWarn(text) {
    const w = this.recordWarn;
    if (!w) return;
    w.textContent = text;
    w.classList.add('visible');
    clearTimeout(this._warnTimer);
    this._warnTimer = setTimeout(() => w.classList.remove('visible'), 2000);
  }

  /**
   * Create/update the record button inside the piano panel's top bar
   * (#piano-record-bar). Called from render() so the button title
   * updates on locale change. The button and warn span are stored as
   * instance properties so the recorder.onChange listener and the
   * warn-target code don't need a DOM query on every event.
   */
  _renderRecordButton() {
    const bar = document.getElementById('piano-record-bar');
    if (!bar) return;
    bar.innerHTML = '';

    const btn = document.createElement('button');
    btn.className = 'piano-record-btn transport-btn-record';
    btn.id = 'btn-record';
    btn.textContent = '●';
    btn.title = t('transport.record');
    btn.addEventListener('click', () => this._onRecordClick());
    this.recordBtn = btn;

    const warn = document.createElement('span');
    warn.id = 'record-warn';
    warn.className = 'record-warn';
    warn.setAttribute('aria-live', 'polite');
    this.recordWarn = warn;

    bar.appendChild(btn);
    bar.appendChild(warn);

    // Sync initial state (button may already be active if recording
    // was toggled before the first render).
    if (typeof recorder !== 'undefined' && recorder.recording) {
      btn.classList.add('active');
    }
  }

  /**
   * Start playback. Two cases:
   *   - Fresh (no pending schedule): build a fresh schedule starting at
   *     the current Transport.seconds (which is 0 after a stop, or
   *     some other position if the user has dragged the playhead).
   *   - Resuming (pending schedule from a previous play that was paused):
   *     re-schedule the snapshot'd sources at adjusted times and restart
   *     the Transport. The Transport's clock resumes from where it
   *     paused, and the new source start times are shifted forward by
   *     the pause duration so the notes still fire at the right
   *     arrangement positions.
   */
  play() {
    if (this.isPlaying) return;
    engine.unmuteMaster();
    // Clear any stale loop handler from a previous play().
    this._unregisterLoopHandler();
    if (this.trackManager.hasPendingSchedule()) {
      // Resume from the current Transport.seconds. Re-trigger the
      // snapshot'd sources with a time shift so they land at the right
      // positions relative to the resumed clock.
      const now = Tone.now();
      const timeShift = now - this._pauseTime;
      this.trackManager.resumeAll(this._pauseTime, timeShift);
      this._pauseTime = 0;
      Tone.Transport.start();
      // If loop is on, re-register the wrap handler. We use the
      // current AudioContext time as the base (rather than the
      // original startAt) since the resumed schedule is already
      // shifted by timeShift.
      if (Tone.Transport.loop) this._registerLoopHandler(now);
    } else {
      // Fresh schedule. startAt is in the near future.
      const startAt = Tone.now() + 0.1;
      Tone.Transport.seconds = 0;
      const endTime = this.trackManager.scheduleAll(startAt);
      Tone.Transport.start(startAt, 0);
      this._scheduleEndTimeout(startAt, endTime);
      // Register the loop-wrap handler so notes are re-scheduled
      // at each loop boundary. We do this here (not in the loop
      // button click) because we need startAt — the AudioContext
      // time of iteration 1's first note — to schedule iteration 2.
      if (Tone.Transport.loop) this._registerLoopHandler(startAt);
    }

    this.isPlaying = true;
    const playBtn = this.root.querySelector('#btn-play');
    if (playBtn) playBtn.textContent = '⏸';
  }

  /**
   * Schedule a one-shot stop when the arrangement reaches its end. */
  _scheduleEndTimeout(startAt, endTime) {
    if (Tone.Transport.loop) return;
    const dur = endTime - startAt;
    if (dur > 0) {
      this.scheduledEndId = setTimeout(() => {
        // The arrangement finished playing on its own. Cancel the
        // schedule so the next play() starts a fresh arrangement from
        // the current playhead position (instead of trying to resume
        // an empty schedule). Note: we deliberately do NOT call
        // engine.muteMaster() here — that flag would suppress any
        // subsequent piano tapping (which uses the same master bus)
        // until the user clicks Play again, which is the only path
        // that calls unmuteMaster().
        this.trackManager.cancelAllScheduled();
        this.trackManager.clearSchedule();
        Tone.Transport.stop();
        // Reset the playhead to 0 so the next play() starts at the
        // beginning. `seconds = 0` is the v14-correct way to jump the
        // playhead to the start (`.position` is in BarsBeatsSixteenths
        // and depends on the time signature).
        Tone.Transport.seconds = 0;
        this.isPlaying = false;
        const playBtn = this.root.querySelector('#btn-play');
        if (playBtn) playBtn.textContent = '▶';
      }, dur * 1000 + 100);
    }
  }

  /**
   * Register a 'loop' event handler on Tone.Transport that
   * re-schedules the next iteration's notes each time the transport
   * wraps. `startAt` is the AudioContext time of iteration 1's first
   * scheduled note; we compute iteration 2's start as
   * startAt + loopLength, iteration 3's as startAt + 2*loopLength,
   * and so on. loopLength is read from Tone.Transport.loopEnd
   * (which was set by the loop button click handler).
   *
   * Cleans up any previously-registered handler first (idempotent).
   */
  _registerLoopHandler(startAt) {
    this._unregisterLoopHandler();
    const loopLength = Tone.Transport.loopEnd - Tone.Transport.loopStart;
    if (loopLength <= 0) return;
    // The first iteration was already scheduled by play(). We start
    // the counter at the next iteration.
    let nextStartAt = startAt + loopLength;
    const handler = () => {
      // Defer with a microtask so the synchronous tick processing
      // isn't blocked by scheduleAll.
      const target = nextStartAt;
      nextStartAt += loopLength;
      Promise.resolve().then(() => {
        this.trackManager.scheduleAll(target);
      });
    };
    this._loopHandler = handler;
    Tone.Transport.on('loop', handler);
  }

  /** Remove the 'loop' event handler, if any. Idempotent. */
  _unregisterLoopHandler() {
    if (this._loopHandler) {
      Tone.Transport.off('loop', this._loopHandler);
      this._loopHandler = null;
    }
  }

  /**
   * Pause playback at the current position. Silences live voices
   * (anything already past its start time is faded out fast) AND
   * disposes every future-scheduled sampler source, snapshotting its
   * parameters so the next play() can re-trigger them at the right
   * times. The progress line does NOT reset.
   *
   * This is necessary because Tone.Transport.pause() does NOT pause
   * the AudioContext clock — Web Audio events scheduled against the
   * AudioContext (e.g. Sampler triggerAttackRelease with a future
   * time) would still fire after Transport.pause(). Disposing the
   * underlying ToneBufferSourceNode is the only way to silence them.
   *
   * We deliberately do NOT call engine.muteMaster() here — that would
   * also silence the live piano keyboard, and the user should be able
   * to audition notes even while the timeline is paused.
   */
  pause() {
    if (!this.isPlaying) return;
    // Capture the AudioContext time before stopping — needed by play()
    // to compute the time shift for re-scheduling the snapshot'd sources.
    this._pauseTime = this.trackManager.pauseAll();
    this._unregisterLoopHandler();
    Tone.Transport.pause();
    this.isPlaying = false;
    if (this.scheduledEndId) clearTimeout(this.scheduledEndId);
    const playBtn = this.root.querySelector('#btn-play');
    if (playBtn) playBtn.textContent = '▶';
  }

  /**
   * Cancel every scheduled note (including future-scheduled ones),
   * silence live voices, and return the progress line to 0:00.0. The
   * next play() will build a fresh schedule from the start.
   */
  stop() {
    this.trackManager.cancelAllScheduled();
    this.trackManager.clearSchedule();
    this._unregisterLoopHandler();
    // Same reasoning as pause() — we keep the master bus unmuted so
    // the piano keyboard keeps producing sound. cancelAllScheduled()
    // already disposes every future-scheduled ToneBufferSourceNode,
    // and releaseAll() (inside stopAll) fades out live voices. The
    // master bus doesn't need to be affected.
    Tone.Transport.stop();
    // `seconds = 0` is the v14-correct way to jump the playhead to the
    // start (`.position` is in BarsBeatsSixteenths and depends on the
    // time signature).
    Tone.Transport.seconds = 0;
    this.isPlaying = false;
    if (this.scheduledEndId) clearTimeout(this.scheduledEndId);
    // Abort any in-progress recording countdown so the overlay doesn't
    // outlive the user's intent to stop.
    this._cancelCountdown();
    const playBtn = this.root.querySelector('#btn-play');
    if (playBtn) playBtn.textContent = '▶';
  }

  _tick() {
    // Transport.pause() preserves the playhead position, so just read it.
    const pos = Tone.Transport.seconds || 0;
    const input = this.root.querySelector('#position-input');
    if (input && document.activeElement !== input) {
      input.value = formatTime(pos);
    }
    requestAnimationFrame(this._tick);
  }
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = (sec - m * 60).toFixed(1);
  return `${m}:${s.padStart(4, '0')}`;
}
